import { normalizeVoiceText } from "../../voice/ownerVoiceParsers";

const SECTION_LABELS = Object.freeze({
  "today-jarvis": "Jarvis / Hoy",
  "today-agenda": "Agenda de hoy",
  "today-money": "Dinero de hoy",
  "project-summary": "Resumen del proyecto",
  "project-progress": "Avance del proyecto",
  "project-steps": "Pendientes / pasos del proyecto",
  "project-economics": "Economía del proyecto",
  "project-agreement": "Acuerdo del proyecto",
  "project-money": "Cobros del proyecto",
  "project-billing": "Billing WebProfit",
  "project-payments": "Próximos cobros",
  "project-work": "Trabajo del proyecto",
  "project-webprofit": "WebProfit vinculado",
  "project-people": "Cliente / personas del proyecto",
  "jarvis-lab": "Jarvis Lab",
});

function rawText(input = {}) {
  return String(input.raw || input.text || "").trim();
}

function explicitExternalId(value) {
  const match = String(value || "").toUpperCase().match(/\b(?:MEJ|FIX)[-\s]?(\d{1,6})\b/);
  return match ? `MEJ-${String(Number(match[1])).padStart(3, "0")}` : "";
}

function visibleOwnerSection() {
  if (typeof document === "undefined") return null;
  const rows = Array.from(document.querySelectorAll("[data-owner-jarvis-section], [data-owner-project-section]"))
    .map((node) => {
      const rect = node.getBoundingClientRect();
      const key = node.getAttribute("data-owner-jarvis-section") || node.getAttribute("data-owner-project-section") || "";
      const visible = rect.bottom > 70 && rect.top < window.innerHeight && rect.width > 0 && rect.height > 0;
      return { key, rect, visible, depth: key.split("-").length };
    })
    .filter((row) => row.visible && row.key)
    .sort((a, b) => Math.abs(a.rect.top - 150) - Math.abs(b.rect.top - 150) || b.depth - a.depth);
  return rows[0]?.key || null;
}

function sourceContext(input = {}) {
  const route = String(input.routeContext || (typeof window !== "undefined" ? window.location.pathname : ""));
  const section = visibleOwnerSection() || "";
  const projectMatch = route.match(/\/dashboard\/owner\/proyectos\/([^/?#]+)/);
  return {
    route,
    surface: projectMatch ? "project360" : route.includes("/jarvis-lab") ? "jarvis_lab" : "owner",
    section,
    sectionLabel: SECTION_LABELS[section] || "",
    entityType: projectMatch ? "project" : "",
    entityId: projectMatch ? decodeURIComponent(projectMatch[1]) : "",
    initiativeId: projectMatch ? decodeURIComponent(projectMatch[1]) : "",
    source: input.inputSource || "jarvis",
  };
}

function priorityFromText(value) {
  const text = normalizeVoiceText(value);
  if (/\b(critico|critica|urgente|grave|bloquea|rompe|error|bug|falla)\b/.test(text)) return "ALTA";
  if (/\b(baja|menor|detalle|cuando puedas)\b/.test(text)) return "BAJA";
  return "MEDIA";
}

function titleFromUtterance(value) {
  const raw = String(value || "").trim().replace(/\s+/g, " ");
  const stripped = raw
    .replace(/^jarvis[,\s:]*/i, "")
    .replace(/^(?:anota|anotá|registra|registrá|carga|cargá|agrega|agregá)\s+(?:un|una|este|esta)?\s*(?:error|bug|fix|mejora)?\s*[:,-]?\s*/i, "")
    .trim();
  const candidate = stripped || raw;
  return candidate.length > 150 ? `${candidate.slice(0, 147)}...` : candidate;
}

function wantsBacklog(value) {
  const text = normalizeVoiceText(value);
  return /\b(jarvis lab|backlog|lista de fixes|lista de mejoras|ver fixes|ver mejoras)\b/.test(text);
}

function looksLikeNewIssue(value) {
  const text = normalizeVoiceText(value);
  if (!text || explicitExternalId(value)) return false;
  if (/\b(anota|registra|carga|agrega)\b/.test(text) && /\b(error|bug|fix|mejora|problema)\b/.test(text)) return true;
  if (/\bjarvis\b/.test(text) && /\b(deberia|tendria que|no hace|no esta haciendo|falla|error|mejora)\b/.test(text)) return true;
  if (/\b(aca|aqui|esta vista|este panel|esto)\b/.test(text) && /\b(error|falla|no funciona|deberia|mejora|rompe)\b/.test(text)) return true;
  return false;
}

function positiveReview(value) {
  const text = normalizeVoiceText(value);
  return /\b(quedo bien|esta bien|funciona bien|solucionado|resuelto|perfecto)\b/.test(text);
}

export function createOwnerJarvisImprovementCapability({ ownerApi, navigate }) {
  return {
    id: "owner-improvement-tracker",
    async handle(input = {}) {
      const raw = rawText(input);
      if (!raw) return null;

      if (wantsBacklog(raw)) {
        navigate("/dashboard/owner/jarvis-lab");
        return { handled: true, message: "Te abro Jarvis Lab. Ahí están la conexión con Google Sheets y los fixes que Jarvis fue capturando." };
      }

      const externalId = explicitExternalId(raw);
      if (externalId) {
        const normalized = normalizeVoiceText(raw);
        const negative = /\b(sigue mal|no funciona|falla|fallo|error|todavia|falta|rompio|no quedo)\b/.test(normalized);
        if (!negative && !positiveReview(raw)) return null;
        const item = await ownerApi.updateJarvisBacklogItem(externalId, negative
          ? { status: "ACTIVO", humanNoteAppend: raw }
          : { status: "SOLUCIONADO", humanNoteAppend: `REVISION HUMANA: ${raw}` });
        return {
          handled: true,
          message: negative
            ? `${externalId} vuelve a ACTIVO y agregué tu devolución sin borrar el resultado ni los intentos anteriores.`
            : `${externalId} queda SOLUCIONADO con tu validación.`,
          highlightSection: "today-jarvis",
          item: item?.item || item,
        };
      }

      if (!looksLikeNewIssue(raw)) return null;
      const context = sourceContext(input);
      const locationText = context.sectionLabel || (context.surface === "project360" ? "la ficha del proyecto" : "Owner");
      const response = await ownerApi.createJarvisBacklogItem({
        priority: priorityFromText(raw),
        title: titleFromUtterance(raw),
        description: raw,
        humanNote: `Capturado por Jarvis desde ${locationText}.`,
        sourceContext: context,
      });
      const item = response?.item || {};
      const label = item.externalId || "el fix local";
      const pending = item?.sync?.state !== "synced";
      return {
        handled: true,
        message: pending
          ? `Anoté ${label} desde ${locationText}. Quedó guardado localmente y pendiente de sincronizar con Google Sheets; no se pierde aunque la conexión esté caída.`
          : `Anoté ${label} desde ${locationText} y ya quedó sincronizado con Google Sheets.`,
        highlightSection: context.section || "today-jarvis",
      };
    },
  };
}
